import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { auth, db } from '../../lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { getWallet, withdraw } from '../../services/wallet';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();

  const handleUpgrade = async (plan: 'basic' | 'pro', period?: 'monthly' | 'quarterly' | 'yearly') => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert('Please sign in to upgrade your plan');
        navigate('/auth/sign-in');
        return;
      }

      if (plan === 'pro') {
        const prices: Record<'monthly' | 'quarterly' | 'yearly', number> = {
          monthly: 2500,
          quarterly: 7000,
          yearly: 42000,
        };
        const chosen = period || 'monthly';
        const upgradeCost = prices[chosen];
        const wallet = await getWallet(user.uid);
        if ((wallet.balance || 0) < upgradeCost) {
          alert('Insufficient balance. Please add money to your wallet to upgrade.');
          navigate('/wallet');
          return;
        }
        // Debit wallet for Premium subscription
        await withdraw(user.uid, upgradeCost, { description: `Premium ${chosen} subscription` });
      }

      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        plan: plan,
        premiumPeriod: period || (plan === 'pro' ? 'monthly' : undefined),
        planUpgradedAt: new Date(),
      });

      alert(`Successfully upgraded to ${plan === 'pro' ? 'Premium' : 'Basic'} plan!`);
      navigate('/seller-dashboard');
    } catch (error) {
      console.error('Error upgrading plan:', error);
      alert('Error upgrading plan. Please try again.');
    }
  };

  const plans = [
    {
      name: 'Basic',
      price: 'Free',
      description: 'Best for occasional sellers',
      features: [
        '0% platform fees (no sales cut)',
        'Pay ₦200 per listing',
        'Basic seller profile',
        'Standard customer support',
        '24-hour payment processing'
      ],
      buttonText: 'Continue with Basic',
      buttonVariant: 'outline',
      popular: false
    },
    {
      name: 'Premium',
      price: '₦2,500',
      period: '/month',
      description: 'For serious campus entrepreneurs',
      features: [
        '0% platform fees (no sales cut)',
        'Unlimited features and listings',
        'Product ranking boost',
        'Verified Premium seller badge',
        'Priority customer support',
        'Instant payment processing',
        'Eligible for featured placement',
        'Advanced analytics dashboard'
      ],
      buttonText: 'Upgrade to Premium (Monthly)',
      buttonVariant: 'primary',
      popular: true
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choose Your Seller Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Whether you're selling a few items or building a campus business, 
            we have the perfect plan for your needs.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={plan.name}
              className={`relative bg-white rounded-2xl shadow-lg p-8 ${
                plan.popular 
                  ? 'ring-2 ring-blue-500 transform scale-105' 
                  : 'border border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  {plan.period && (
                    <span className="text-gray-600 ml-1">{plan.period}</span>
                  )}
                </div>
                <p className="text-gray-600">{plan.description}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center">
                    <i className="ri-check-line text-green-500 text-lg mr-3"></i>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => (plan.name === 'Premium' ? handleUpgrade('pro', 'monthly') : handleUpgrade('basic'))}
                className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
                  plan.buttonVariant === 'primary'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {plan.buttonText}
              </button>
              {plan.name === 'Premium' && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => handleUpgrade('pro', 'quarterly')}
                    className="text-sm px-3 py-2 rounded border border-gray-300 hover:bg-gray-50"
                  >
                    Quarterly ₦7,000
                  </button>
                  <button
                    onClick={() => handleUpgrade('pro', 'yearly')}
                    className="text-sm px-3 py-2 rounded border border-gray-300 hover:bg-gray-50"
                  >
                    Yearly ₦42,000
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="max-w-4xl mx-auto mt-20">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Plan Comparison
          </h2>
          
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">Basic</th>
                  <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">Premium</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                 <tr>
                   <td className="px-6 py-4 text-sm text-gray-900">Active Listings</td>
                   <td className="px-6 py-4 text-center text-sm text-gray-600">5</td>
                   <td className="px-6 py-4 text-center text-sm text-gray-600">Unlimited</td>
                 </tr>
                 <tr>
                   <td className="px-6 py-4 text-sm text-gray-900">Platform Fee</td>
                   <td className="px-6 py-4 text-center text-sm text-gray-600">0% (no sales cut)</td>
                   <td className="px-6 py-4 text-center text-sm text-gray-600">0% (no sales cut)</td>
                 </tr>
                 <tr>
                   <td className="px-6 py-4 text-sm text-gray-900">Verified Badge</td>
                   <td className="px-6 py-4 text-center text-sm text-gray-600">-</td>
                   <td className="px-6 py-4 text-center text-sm text-green-600">✓</td>
                 </tr>
                 <tr>
                   <td className="px-6 py-4 text-sm text-gray-900">Priority Support</td>
                   <td className="px-6 py-4 text-center text-sm text-gray-600">-</td>
                   <td className="px-6 py-4 text-center text-sm text-green-600">✓</td>
                 </tr>
                 <tr>
                   <td className="px-6 py-4 text-sm text-gray-900">Payment Processing</td>
                   <td className="px-6 py-4 text-center text-sm text-gray-600">24 hours</td>
                   <td className="px-6 py-4 text-center text-sm text-gray-600">Instant</td>
                 </tr>
                 <tr>
                   <td className="px-6 py-4 text-sm text-gray-900">Featured Placement</td>
                   <td className="px-6 py-4 text-center text-sm text-gray-600">-</td>
                   <td className="px-6 py-4 text-center text-sm text-green-600">✓</td>
                 </tr>
                 <tr>
                   <td className="px-6 py-4 text-sm text-gray-900">Analytics Dashboard</td>
                   <td className="px-6 py-4 text-center text-sm text-gray-600">Basic</td>
                   <td className="px-6 py-4 text-center text-sm text-green-600">Advanced</td>
                 </tr>
               </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mt-20">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Can I switch between plans?
              </h3>
              <p className="text-gray-600">
                 Yes! You can upgrade to Premium anytime. If you move back to Basic, keep active listings at 5 or fewer.
                 Both Basic and Premium sellers list for free.
               </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                 How is billing handled?
               </h3>
               <p className="text-gray-600">
                 We take 0% from every sale. Listing is free on both plans.
                 Premium is subscription-based with monthly, quarterly, or yearly options for extra perks.
               </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                 Is there a free trial for Premium?
               </h3>
               <p className="text-gray-600">
                 No free trial currently. Start with Monthly at ₦2,500 to try Premium.
               </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;